import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, Receipt, Download, Upload, Filter, Calendar, 
  DollarSign, Tag, Trash2, Edit, FileText,
  CheckCircle, Clock, TrendingUp, Repeat, AlertTriangle, Zap, Bell, PieChart
} from 'lucide-react';
import ReceiptCamera from '../components/ReceiptCamera';
import { generateExpenseReportPDF } from '../utils/PDFGenerator';
import {
  EXPENSE_CATEGORIES,
  PAYMENT_METHODS,
  QUICK_EXPENSES,
  DEFAULT_BUDGETS,
} from '../utils/expenseHelpers';
import ExpenseFormModal from '../components/expenses/ExpenseFormModal';
import RecurringExpenseModal from '../components/expenses/RecurringExpenseModal';
import BudgetModal from '../components/expenses/BudgetModal';
import ReportsTaxes from './ReportsTaxes';
import { IconTile, TINT } from '../components/ui/IconTile';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Expenses = () => {
  // ?tab=reports deep-links the Reports & Taxes sub-view so the legacy
  // /reports-taxes route + sidebar shortcut still resolve correctly.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'reports' ? 'reports' : 'expenses';
  const [activeTab, setActiveTab] = useState(initialTab);
  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'reports') setSearchParams({ tab: 'reports' }, { replace: true });
    else setSearchParams({}, { replace: true });
  };

  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [bankTransactions, setBankTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDateRange, setFilterDateRange] = useState({ start: '', end: '' });
  
  // New feature states
  const [showQuickExpense, setShowQuickExpense] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [budgetAlerts, setBudgetAlerts] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    merchant: '',
    category: '',
    description: '',
    receipt_image: '',
    payment_method: 'Cash',
    tax_deductible: true
  });

  // CSV Import state
  const [csvData, setCsvData] = useState('');
  
  // Recurring expense form state
  const [recurringForm, setRecurringForm] = useState({
    name: '',
    amount: '',
    category: '',
    frequency: 'monthly', // monthly, weekly, yearly
    startDate: new Date().toISOString().split('T')[0],
    description: ''
  });

  useEffect(() => {
    loadExpenses();
    loadBankTransactions();
    loadRecurringExpenses();
    loadBudgets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  
  // Check budget alerts when expenses change
  useEffect(() => {
    checkBudgetAlerts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, budgets]);
  
  const loadRecurringExpenses = () => {
    const saved = localStorage.getItem(`maidly_recurring_expenses_${user?.id}`);
    if (saved) {
      const recurring = JSON.parse(saved);
      setRecurringExpenses(recurring);
      // Process any due recurring expenses
      processRecurringExpenses(recurring);
    }
  };
  
  const loadBudgets = () => {
    const saved = localStorage.getItem(`maidly_budgets_${user?.id}`);
    if (saved) {
      setBudgets(JSON.parse(saved));
    } else {
      setBudgets(DEFAULT_BUDGETS);
    }
  };
  
  const saveBudgets = (newBudgets) => {
    setBudgets(newBudgets);
    localStorage.setItem(`maidly_budgets_${user?.id}`, JSON.stringify(newBudgets));
  };
  
  const processRecurringExpenses = async (recurring) => {
    const today = new Date().toISOString().split('T')[0];
    let processed = false;
    
    for (const exp of recurring) {
      if (shouldCreateRecurring(exp, today)) {
        // Create the expense
        await handleQuickExpenseAdd({
          name: exp.name,
          amount: exp.amount,
          category: exp.category
        }, exp.description);
        
        // Update last created date
        exp.lastCreated = today;
        processed = true;
      }
    }
    
    if (processed) {
      localStorage.setItem(`maidly_recurring_expenses_${user?.id}`, JSON.stringify(recurring));
      setRecurringExpenses([...recurring]);
      toast.success('Recurring expenses processed!');
    }
  };
  
  const shouldCreateRecurring = (exp, today) => {
    if (!exp.lastCreated) return true;
    
    const last = new Date(exp.lastCreated);
    const now = new Date(today);
    const diffDays = Math.floor((now - last) / (1000 * 60 * 60 * 24));
    
    switch (exp.frequency) {
      case 'daily': return diffDays >= 1;
      case 'weekly': return diffDays >= 7;
      case 'monthly': return diffDays >= 30;
      case 'yearly': return diffDays >= 365;
      default: return false;
    }
  };
  
  const checkBudgetAlerts = () => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const alerts = [];
    
    EXPENSE_CATEGORIES.forEach(category => {
      const monthlyTotal = expenses
        .filter(e => e.category === category && e.date?.startsWith(currentMonth))
        .reduce((sum, e) => sum + (e.amount || 0), 0);
      
      const budget = budgets[category] || DEFAULT_BUDGETS[category];
      const percentage = (monthlyTotal / budget) * 100;
      
      if (percentage >= 90) {
        alerts.push({
          category,
          spent: monthlyTotal,
          budget,
          percentage,
          status: percentage >= 100 ? 'exceeded' : 'warning'
        });
      }
    });
    
    setBudgetAlerts(alerts);
  };
  
  const handleQuickExpenseAdd = async (quickExp, customDescription = '') => {
    const expenseData = {
      user_id: user.id,
      amount: quickExp.amount,
      date: new Date().toISOString().split('T')[0],
      merchant: quickExp.name,
      category: quickExp.category,
      description: customDescription || `Quick expense: ${quickExp.name}`,
      payment_method: 'Cash',
      tax_deductible: true
    };
    
    try {
      const response = await fetch(`${API_URL}/api/expenses/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(expenseData)
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`${quickExp.name} expense added!`);
        loadExpenses();
      }
    } catch (error) {
      console.error('Error adding quick expense:', error);
      toast.error('Failed to add expense');
    }
  };
  
  const addRecurringExpense = () => {
    if (!recurringForm.name || !recurringForm.amount || !recurringForm.category) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    const newRecurring = {
      id: Date.now().toString(),
      ...recurringForm,
      amount: parseFloat(recurringForm.amount),
      createdAt: new Date().toISOString(),
      lastCreated: null
    };
    
    const updated = [...recurringExpenses, newRecurring];
    setRecurringExpenses(updated);
    localStorage.setItem(`maidly_recurring_expenses_${user?.id}`, JSON.stringify(updated));
    
    toast.success('Recurring expense added!');
    setShowRecurringModal(false);
    setRecurringForm({
      name: '',
      amount: '',
      category: '',
      frequency: 'monthly',
      startDate: new Date().toISOString().split('T')[0],
      description: ''
    });
  };
  
  const deleteRecurringExpense = (id) => {
    const updated = recurringExpenses.filter(e => e.id !== id);
    setRecurringExpenses(updated);
    localStorage.setItem(`maidly_recurring_expenses_${user?.id}`, JSON.stringify(updated));
    toast.success('Recurring expense removed');
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/expenses/list/${user.id}`);
      const data = await response.json();
      
      if (data.success) {
        setExpenses(data.expenses);
      }
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const loadBankTransactions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/expenses/transactions/${user.id}`);
      const data = await response.json();
      
      if (data.success) {
        setBankTransactions(data.transactions);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  const handleReceiptCapture = (imageData) => {
    setFormData({ ...formData, receipt_image: imageData });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || !formData.merchant || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const expenseData = {
        ...formData,
        user_id: user.id,
        amount: parseFloat(formData.amount)
      };

      let response;
      if (editingExpense) {
        response = await fetch(`${API_URL}/api/expenses/update/${editingExpense.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(expenseData)
        });
      } else {
        response = await fetch(`${API_URL}/api/expenses/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(expenseData)
        });
      }

      const data = await response.json();

      if (response.ok || data.success) {
        toast.success(editingExpense ? 'Expense updated!' : 'Expense added!');
        loadExpenses();
        resetForm();
        setShowAddModal(false);
      } else {
        toast.error(data.error || 'Failed to save expense');
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Failed to save expense');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      const response = await fetch(`${API_URL}/api/expenses/delete/${expenseId}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Expense deleted!');
        loadExpenses();
      } else {
        toast.error('Failed to delete expense');
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      amount: expense.amount,
      date: expense.date,
      merchant: expense.merchant,
      category: expense.category,
      description: expense.description || '',
      receipt_image: expense.receipt_image || '',
      payment_method: expense.payment_method || 'Cash',
      tax_deductible: expense.tax_deductible !== false
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      date: new Date().toISOString().split('T')[0],
      merchant: '',
      category: '',
      description: '',
      receipt_image: '',
      payment_method: 'Cash',
      tax_deductible: true
    });
    setEditingExpense(null);
  };

  /**
   * Coerce any common date shape (`MM/DD/YYYY`, `M-D-YY`, `YYYY-MM-DD`, ISO
   * timestamp) into a `YYYY-MM-DD` string. Backend `date` field is a raw
   * ISO date string so this keeps sorting/filtering predictable.
   */
  const normalizeCsvDate = (raw) => {
    if (!raw) return '';
    const trimmed = String(raw).trim().replace(/^"|"$/g, '');
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const slash = trimmed.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2}|\d{4})$/);
    if (slash) {
      let [, m, d, y] = slash;
      if (y.length === 2) y = (Number(y) > 50 ? '19' : '20') + y;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
    return '';
  };

  // RFC-4180-lite: honours quoted fields so `"Costco, Wholesale"` stays one column.
  const splitCsvLine = (line) => {
    const out = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (c === ',' && !inQuotes) {
        out.push(cur); cur = '';
      } else {
        cur += c;
      }
    }
    out.push(cur);
    return out.map((v) => v.trim());
  };

  // Best-effort auto-categorisation from merchant keywords.
  const guessCategory = (merchant) => {
    const m = (merchant || '').toLowerCase();
    if (/(uber|lyft|taxi|parking|airline|delta|united|hotel|airbnb|flight)/.test(m)) return '✈️ Travel & Transportation';
    if (/(shell|exxon|chevron|bp|gas|fuel|mobil|76 )/.test(m)) return '🚗 Vehicle & Fuel';
    if (/(restaurant|cafe|coffee|starbucks|mcdonald|pizza|chipotle|uber eats|doordash|grubhub|dining)/.test(m)) return '🍽️ Meals & Entertainment';
    if (/(staples|office depot|amazon|walmart|costco|target)/.test(m)) return '📦 Office Supplies';
    if (/(comcast|verizon|at&t|t-mobile|spectrum|internet|phone)/.test(m)) return '📱 Phone & Internet';
    if (/(electric|water|utility|rent|lease|con ed|pg&e)/.test(m)) return '🏢 Rent & Utilities';
    if (/(repair|maintenance|plumber|hvac|handyman)/.test(m)) return '🛠️ Repairs & Maintenance';
    if (/(udemy|coursera|training|course|book)/.test(m)) return '📚 Training & Education';
    if (/(law|attorney|accountant|consult|legal)/.test(m)) return '💼 Professional Services';
    return '💰 Other Business Expenses';
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 2) {
      toast.error('CSV file is empty or invalid');
      return [];
    }

    const headers = splitCsvLine(lines[0]).map((h) => h.toLowerCase());
    const dateIdx = headers.findIndex((h) => h.includes('date'));
    const descIdx = headers.findIndex((h) => h.includes('description') || h.includes('merchant') || h.includes('name') || h.includes('payee'));
    const amountIdx = headers.findIndex((h) => h.includes('amount') || h.includes('debit') || h.includes('withdrawal'));
    const categoryIdx = headers.findIndex((h) => h.includes('category') || h.includes('type'));

    if (dateIdx < 0 || descIdx < 0 || amountIdx < 0) {
      toast.error('CSV must include date, description, and amount columns');
      return [];
    }

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const values = splitCsvLine(lines[i]);
      const rawAmount = values[amountIdx] || '';
      const amount = parseFloat(rawAmount.replace(/[^0-9.-]/g, ''));
      if (Number.isNaN(amount) || amount === 0) continue;

      const merchant = (values[descIdx] || 'Imported Expense').replace(/^"|"$/g, '');
      const date = normalizeCsvDate(values[dateIdx]) || new Date().toISOString().split('T')[0];
      const category = (categoryIdx >= 0 && values[categoryIdx]) ? values[categoryIdx] : guessCategory(merchant);

      rows.push({
        date,
        merchant,
        amount: Math.abs(amount),
        category,
      });
    }

    return rows;
  };

  const handleImportCSV = async () => {
    if (!csvData) {
      toast.error('Please paste CSV data');
      return;
    }
    if (!user?.id) {
      toast.error('Please log in to import expenses');
      return;
    }

    const rows = parseCSV(csvData);
    if (rows.length === 0) {
      toast.error('No valid expenses found in CSV');
      return;
    }

    const toastId = toast.loading(`Importing ${rows.length} expense${rows.length === 1 ? '' : 's'}…`);
    let imported = 0;
    let failed = 0;

    // Sequential to keep clear per-row errors and avoid backend races.
    for (const row of rows) {
      try {
        const res = await fetch(`${API_URL}/api/expenses/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            user_id: user.id,
            amount: row.amount,
            date: row.date,
            merchant: row.merchant,
            category: row.category,
            description: 'Imported from CSV',
            payment_method: 'Bank Transfer',
            tax_deductible: true,
          }),
        });
        if (res.ok) imported++;
        else failed++;
      } catch (err) {
        console.error('CSV row failed:', err);
        failed++;
      }
    }

    toast.dismiss(toastId);
    if (imported > 0) {
      toast.success(`Imported ${imported} expense${imported === 1 ? '' : 's'}${failed ? ` (${failed} failed)` : ''}`);
      await loadExpenses();
      setShowImportModal(false);
      setCsvData('');
    } else {
      toast.error('Failed to import expenses. Check the CSV format.');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvData(event.target.result);
        toast.success('CSV file loaded!');
      };
      reader.readAsText(file);
    }
  };

  const downloadReport = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/expenses/report/${user.id}?start_date=${filterDateRange.start || ''}&end_date=${filterDateRange.end || ''}`
      );
      const data = await response.json();

      if (data.success) {
        const report = data.report;
        
        // Generate PDF Report
        generateExpenseReportPDF({
          totalCount: report.total_expenses,
          totalAmount: report.total_amount,
          taxDeductible: report.total_deductible,
          nonDeductible: report.total_non_deductible,
          byCategory: report.by_category,
          expenses: report.expenses
        }, filterDateRange);
        
        toast.success('PDF Report downloaded!');
        setShowReportModal(false);
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  const filteredExpenses = expenses.filter(expense => {
    if (filterCategory && expense.category !== filterCategory) return false;
    if (filterDateRange.start && expense.date < filterDateRange.start) return false;
    if (filterDateRange.end && expense.date > filterDateRange.end) return false;
    return true;
  });

  const totalAmount = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);

  return (
    <div style={{ padding: '0 0 2rem 0' }} data-testid="expenses-page">
      {/* Tabs: Expenses ↔ Reports & Taxes (combined view) */}
      <div
        role="tablist"
        aria-label="Expense views"
        style={{
          display: 'inline-flex', gap: 6, padding: 5, marginBottom: 16,
          background: '#f1f5f9', borderRadius: 12, border: '1px solid #e2e8f0',
          boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.04)',
        }}
      >
        {[
          { id: 'expenses', label: 'Expenses', icon: Receipt, tint: TINT.expenses },
          { id: 'reports', label: 'Reports & Taxes', icon: PieChart, tint: TINT.reports },
        ].map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              data-testid={`expenses-tab-${t.id}`}
              onClick={() => switchTab(t.id)}
              style={{
                all: 'unset', cursor: 'pointer',
                padding: '8px 18px 8px 8px', borderRadius: 9,
                fontSize: 13, fontWeight: 700, letterSpacing: 0.1,
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: isActive ? '#0073ea' : 'transparent',
                color: isActive ? '#ffffff' : '#475569',
                boxShadow: isActive ? '0 3px 10px rgba(0,115,234,0.28)' : 'none',
                transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(15,23,42,0.05)'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <IconTile icon={Icon} tint={isActive ? 'rgba(255,255,255,0.22)' : t.tint} size={28} iconSize={15} />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'reports' ? (
        <ReportsTaxes />
      ) : (
      <>
      {/* Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#1a1a1a', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <IconTile icon={Receipt} tint={TINT.expenses} size={44} />
            Expense Management
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Track expenses, scan receipts, and generate tax reports
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Add Expense — red primary (matches Expenses sidebar tint) */}
          <button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            data-testid="add-expense-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem 0.55rem 0.55rem',
              background: TINT.expenses, color: '#fff', border: 'none', borderRadius: 12,
              fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
              boxShadow: `0 6px 16px ${TINT.expenses}55`,
              transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
              letterSpacing: 0.1,
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#dc2626';
              e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.expenses}88`;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = TINT.expenses;
              e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.expenses}55`;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Plus} tint="rgba(255,255,255,0.22)" size={30} />
            Add Expense
          </button>

          {/* Quick Add — amber outline */}
          <button
            onClick={() => setShowQuickExpense(!showQuickExpense)}
            data-testid="quick-add-toggle"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 600, color: showQuickExpense ? '#f59e0b' : '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#f59e0b0d';
              e.currentTarget.style.borderColor = '#f59e0b66';
              e.currentTarget.style.color = '#f59e0b';
              e.currentTarget.style.boxShadow = '0 6px 16px #f59e0b33';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = showQuickExpense ? '#f59e0b' : '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Zap} tint="#f59e0b" size={30} />
            Quick Add
          </button>

          {/* Recurring — purple outline */}
          <button
            onClick={() => setShowRecurringModal(true)}
            data-testid="open-recurring-modal"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 600, color: '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#7c3aed0d';
              e.currentTarget.style.borderColor = '#7c3aed66';
              e.currentTarget.style.color = '#7c3aed';
              e.currentTarget.style.boxShadow = '0 6px 16px #7c3aed33';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Repeat} tint="#7c3aed" size={30} />
            Recurring
          </button>

          {/* Budgets — emerald outline (or red when alerts) */}
          <button
            onClick={() => setShowBudgetModal(true)}
            data-testid="open-budget-modal"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              cursor: 'pointer', position: 'relative',
              fontSize: '0.9rem', fontWeight: 600, color: '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              const c = budgetAlerts.length > 0 ? '#ef4444' : '#059669';
              e.currentTarget.style.background = `${c}0d`;
              e.currentTarget.style.borderColor = `${c}66`;
              e.currentTarget.style.color = c;
              e.currentTarget.style.boxShadow = `0 6px 16px ${c}33`;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Bell} tint={budgetAlerts.length > 0 ? '#ef4444' : '#059669'} size={30} />
            Budgets
            {budgetAlerts.length > 0 && (
              <span style={{
                position: 'absolute', top: '-6px', right: '-6px',
                background: '#ef4444', color: '#fff',
                borderRadius: '50%', width: '20px', height: '20px',
                fontSize: '0.75rem', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(239,68,68,0.45)',
              }}>
                {budgetAlerts.length}
              </span>
            )}
          </button>

          {/* Import CSV — orange outline */}
          <button
            onClick={() => setShowImportModal(true)}
            data-testid="expense-import-csv-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 600, color: '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#f973160d';
              e.currentTarget.style.borderColor = '#f9731666';
              e.currentTarget.style.color = '#f97316';
              e.currentTarget.style.boxShadow = '0 6px 16px #f9731633';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Upload} tint="#f97316" size={30} />
            Import CSV
          </button>

          {/* Export Report — blue outline (matches Time Clock tint) */}
          <button
            onClick={() => setShowReportModal(true)}
            data-testid="expense-export-report-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 600, color: '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#3b82f60d';
              e.currentTarget.style.borderColor = '#3b82f666';
              e.currentTarget.style.color = TINT.timeClock;
              e.currentTarget.style.boxShadow = '0 6px 16px #3b82f633';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Download} tint={TINT.timeClock} size={30} />
            Export Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <div style={{ background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)', padding: '1.5rem', borderRadius: '12px', color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <DollarSign size={24} />
            <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Expenses</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>${totalAmount.toFixed(2)}</div>
        </div>

        <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', color: '#6b7280' }}>
            <Receipt size={24} />
            <span style={{ fontSize: '0.875rem' }}>Total Count</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1a1a1a' }}>{filteredExpenses.length}</div>
        </div>

        <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', color: '#6b7280' }}>
            <TrendingUp size={24} />
            <span style={{ fontSize: '0.875rem' }}>Tax Deductible</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#10b981' }}>
            ${filteredExpenses.filter(e => e.tax_deductible).reduce((sum, e) => sum + e.amount, 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Quick Expense Panel */}
      {showQuickExpense && (
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          border: '2px solid #f59e0b'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <Zap size={20} color="#d97706" />
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#92400e', margin: 0 }}>Quick Add Expense</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
            {QUICK_EXPENSES.map((qe) => (
              <button
                key={qe.name}
                onClick={() => handleQuickExpenseAdd(qe)}
                style={{
                  padding: '1rem',
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <span style={{ fontSize: '1.5rem' }}>{qe.icon}</span>
                <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>{qe.name}</span>
                <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 600 }}>${qe.amount.toFixed(2)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Budget Alerts */}
      {budgetAlerts.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          border: '2px solid #f87171'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <AlertTriangle size={20} color="#dc2626" />
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#991b1b', margin: 0 }}>Budget Alerts</h3>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            {budgetAlerts.map((alert) => (
              <div key={alert.category} style={{
                padding: '0.5rem 1rem',
                background: alert.status === 'exceeded' ? '#dc2626' : '#f59e0b',
                color: '#fff',
                borderRadius: '20px',
                fontSize: '0.8rem',
                fontWeight: 500
              }}>
                {alert.category}: ${alert.spent.toFixed(0)} / ${alert.budget} ({alert.percentage.toFixed(0)}%)
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', marginBottom: '1.5rem', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <Filter size={20} color="#6b7280" />
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1a1a1a', margin: 0 }}>Filters</h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
              Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '0.625rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="">All Categories</option>
              {EXPENSE_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
              Start Date
            </label>
            <input
              type="date"
              value={filterDateRange.start}
              onChange={(e) => setFilterDateRange({ ...filterDateRange, start: e.target.value })}
              style={{
                width: '100%',
                padding: '0.625rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
              End Date
            </label>
            <input
              type="date"
              value={filterDateRange.end}
              onChange={(e) => setFilterDateRange({ ...filterDateRange, end: e.target.value })}
              style={{
                width: '100%',
                padding: '0.625rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => {
                setFilterCategory('');
                setFilterDateRange({ start: '', end: '' });
              }}
              data-testid="clear-filters-button"
              style={{
                padding: '0.625rem 1.25rem',
                background: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Expenses List */}
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>Loading expenses...</div>
        ) : filteredExpenses.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <Receipt size={48} color="#d1d5db" style={{ margin: '0 auto 1rem' }} />
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No expenses found. Add your first expense to get started!</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Merchant</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Category</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Amount</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Payment</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Receipt</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((expense, index) => (
                  <tr key={expense.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#1a1a1a' }}>{expense.date}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#1a1a1a', fontWeight: 500 }}>{expense.merchant}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>{expense.category}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#1a1a1a', fontWeight: 600 }}>${expense.amount.toFixed(2)}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>{expense.payment_method}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: expense.status === 'Matched' ? '#d1fae5' : '#e0f2ff',
                        color: expense.status === 'Matched' ? '#065f46' : '#0369a1'
                      }}>
                        {expense.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {expense.receipt_image && (
                        <button
                          onClick={() => window.open(expense.receipt_image, '_blank')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          <Receipt size={18} color="#0073ea" />
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleEdit(expense)}
                          style={{
                            padding: '0.5rem',
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Edit size={16} color="#6b7280" />
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          style={{
                            padding: '0.5rem',
                            background: '#fef2f2',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Trash2 size={16} color="#dc2626" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Expense Modal */}
      <ExpenseFormModal
        open={showAddModal}
        editingExpense={editingExpense}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        onClose={() => { setShowAddModal(false); resetForm(); }}
        onReceiptCapture={handleReceiptCapture}
      />


      {/* Import CSV Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            padding: '2rem'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem', color: '#1a1a1a' }}>
              Import Expenses from CSV
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
              Upload or paste a CSV with <strong>date</strong>, <strong>description</strong>, and <strong>amount</strong> columns (a <strong>category</strong> column is optional). Categories are auto-detected from the merchant when omitted.
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '1rem',
                border: '2px dashed #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: '1rem'
              }}>
                <Upload size={20} />
                Choose CSV File
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </label>

              <div style={{ textAlign: 'center', margin: '1rem 0', color: '#9ca3af', fontSize: '0.875rem' }}>OR</div>

              <textarea
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
                placeholder="Paste CSV data here..."
                rows="8"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleImportCSV}
                disabled={!csvData}
                data-testid="expense-import-csv-submit"
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: csvData ? 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)' : '#e5e7eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: csvData ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem'
                }}
              >
                Import Expenses
              </button>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setCsvData('');
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Report Modal */}
      {showReportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%',
            padding: '2rem'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem', color: '#1a1a1a' }}>
              Export Expense Report
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
              Generate a comprehensive expense report with category summaries and detailed transactions.
            </p>

            <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                  Start Date (Optional)
                </label>
                <input
                  type="date"
                  value={filterDateRange.start}
                  onChange={(e) => setFilterDateRange({ ...filterDateRange, start: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                  End Date (Optional)
                </label>
                <input
                  type="date"
                  value={filterDateRange.end}
                  onChange={(e) => setFilterDateRange({ ...filterDateRange, end: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={downloadReport}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Download size={18} />
                Download Report
              </button>
              <button
                onClick={() => setShowReportModal(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recurring Expenses Modal */}
      <RecurringExpenseModal
        open={showRecurringModal}
        recurringForm={recurringForm}
        setRecurringForm={setRecurringForm}
        recurringExpenses={recurringExpenses}
        onAdd={addRecurringExpense}
        onDelete={deleteRecurringExpense}
        onClose={() => setShowRecurringModal(false)}
      />


      {/* Budget Management Modal */}
      <BudgetModal
        open={showBudgetModal}
        expenses={expenses}
        budgets={budgets}
        onSaveBudgets={saveBudgets}
        onClose={() => setShowBudgetModal(false)}
      />

      </>
      )}
    </div>
  );
};

export default Expenses;
